import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';import Button from 'shared/components/UC/Button';
import Toast from 'shared/components/Toast';
import PageHeader from '../components/PageHeader';
import evaluatorService from '../services/evaluatorService';
import { EvaluatorApplication } from '../types/evaluator';
import styles from './EvaluatorDashboard.module.css';

type DeclineReason = 'thematic' | 'conflict' | 'other';

const DECLINE_REASON_LABELS: Record<DeclineReason, string> = {
  thematic: 'Área no afín',
  conflict: 'Conflicto de interés',
  other: 'Otro motivo',
};

const EvaluatorDashboard: React.FC = () => {  const navigate = useNavigate();
  const [applications, setApplications] = useState<EvaluatorApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noNewInvitations, setNoNewInvitations] = useState(false);

  useEffect(() => {
    const loadApplications = async () => {
      try {
        setLoading(true);
        const assignedApplications =
          await evaluatorService.getAssignedApplications();
        setApplications(assignedApplications);
      } catch (err) {
        console.error('Error loading assigned applications:', err);
        setError('Error al cargar las postulaciones asignadas');
      } finally {
        setLoading(false);
      }
    };

    loadApplications();
  }, []);

  const getAssignmentStatusBadge = (application: EvaluatorApplication) => {
    switch (application.assignment.status) {
      case 'conflict_declared':
        return (
          <span className={styles.assignmentConflict}>Conflicto Declarado</span>
        );
      case 'completed':
        return <span className={styles.assignmentCompleted}>Completada</span>;
      case 'declined':
        return <span className={styles.assignmentDeclined}>Declinada</span>;
      case 'pending':
        return <span className={styles.assignmentAssigned}>Por responder</span>;
      case 'accepted_not_started':
      default:
        return <span className={styles.assignmentAccepted}>Aceptada</span>;
    }
  };

  const getEvaluationStatusBadge = (application: EvaluatorApplication) => {
    const status = application.assignment.status;
    if (status === 'declined') {
      return <span className={styles.evaluationNA}>—</span>;
    }
    if (
      status === 'conflict_declared' ||
      application.assignment.conflictStatus === 'conflict_declared'
    ) {
      return <span className={styles.evaluationConflict}>No Evaluable</span>;
    }
    if (application.evaluation?.submittedAt) {
      return <span className={styles.evaluationCompleted}>Completada</span>;
    }
    // Invitation not answered yet — the evaluation doesn't apply until accepted.
    if (status === 'pending') {
      return <span className={styles.evaluationNA}>—</span>;
    }
    // Accepted / in progress: the no-conflict declaration happened when
    // accepting the invitation, so the evaluation is simply pending.
    return <span className={styles.evaluationPending}>Pendiente</span>;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No disponible';
    return new Date(dateString).toLocaleDateString('es-CL');
  };

  const handleEvaluateClick = (applicationId: string) => {
    navigate(`/evaluator/evaluate/${applicationId}`);
  };

  // Pending invitations are answered from the invitation landing — the same
  // page the email links to — so both entry points share one flow.
  const handleViewInvitationClick = (app: EvaluatorApplication) => {
    navigate(`/evaluator/invitation/${app.assignment.id}`);
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const prevCount = applications.length;
      const assignedApplications =
        await evaluatorService.getAssignedApplications();
      const newCount = assignedApplications.length;

      setApplications(assignedApplications);

      // No new invitations if count didn't change.
      if (newCount === prevCount) {
        setNoNewInvitations(true);
        setTimeout(() => setNoNewInvitations(false), 4000);
      }
    } catch (err) {
      console.error('Error refreshing applications:', err);
      setError('Error al actualizar las postulaciones');
    } finally {
      setRefreshing(false);
    }
  };

  const calculateStats = () => {
    const total = applications.length;
    const declined = applications.filter(
      (app) => app.assignment.status === 'declined'
    ).length;
    const completed = applications.filter(
      (app) => app.evaluation?.submittedAt
    ).length;
    const conflicts = applications.filter(
      (app) =>
        app.assignment.status === 'conflict_declared' ||
        app.assignment.conflictStatus === 'conflict_declared'
    ).length;
    // Evaluations pending = accepted (not yet submitted). The no-conflict
    // declaration is part of accepting, so it's no longer a separate state.
    const pending = applications.filter(
      (app) =>
        (app.assignment.status === 'accepted_not_started' ||
          app.assignment.status === 'in_progress') &&
        !app.evaluation?.submittedAt
    ).length;
    // Invitaciones aún sin responder (ni aceptadas ni declinadas). Faltaban en el
    // desglose: por eso el total (todas las asignaciones) no cuadraba con la suma.
    const pendingResponse = applications.filter(
      (app) => app.assignment.status === 'pending'
    ).length;

    return { total, completed, pending, pendingResponse, conflicts, declined };
  };

  const stats = calculateStats();

  // Desglose para la frase resumen. Categorías exhaustivas y mutuamente
  // excluyentes: su suma cuadra con el total de asignadas.
  const summaryItems = [
    { n: stats.pendingResponse, one: 'por responder', many: 'por responder' },
    { n: stats.pending, one: 'pendiente', many: 'pendientes' },
    { n: stats.completed, one: 'completada', many: 'completadas' },
    { n: stats.declined, one: 'declinada', many: 'declinadas' },
    { n: stats.conflicts, one: 'con conflicto', many: 'con conflictos' },
  ].filter((it) => it.n > 0);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <h2>Cargando postulaciones...</h2>
          <p>Por favor espere mientras se cargan sus asignaciones.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <PageHeader
        title="Panel de Evaluación"
        subtitle="Gestión de evaluaciones de postulaciones asignadas"      />

      <div className={styles.container}>
        <p className={styles.summary}>
          Tiene <strong>{stats.total}</strong>{' '}
          {stats.total === 1
            ? 'postulación asignada'
            : 'postulaciones asignadas'}
          {summaryItems.length > 0 && ': '}
          {summaryItems.map((it, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && (idx === summaryItems.length - 1 ? ' y ' : ', ')}
              <strong>{it.n}</strong> {it.n === 1 ? it.one : it.many}
            </React.Fragment>
          ))}
          .
        </p>

        <div className={styles.content}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionHeaderTop}>
              <div className={styles.sectionHeaderText}>
                <h2>Postulaciones Asignadas</h2>
                <p>Listado de postulaciones asignadas para evaluación</p>
              </div>
              <button
                className={styles.refreshButton}
                onClick={handleRefresh}
                disabled={refreshing}
                title="Actualizar"
              >
                <i className="uc-icon icon-size--sm">
                  {refreshing ? 'cached' : 'refresh'}
                </i>
              </button>
            </div>
          </div>

          {applications.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📝</div>
              <h3 className={styles.emptyTitle}>
                No hay postulaciones asignadas
              </h3>
              <p className={styles.emptyDescription}>
                Actualmente no tienes postulaciones asignadas para evaluar. Las
                nuevas asignaciones aparecerán aquí cuando estén disponibles.
              </p>
            </div>
          ) : (
            <div className={styles.applicationsList}>
              <div className={styles.applicationsTable}>
                <div className={styles.tableHeader}>
                  <div className={styles.headerCell}>Folio</div>
                  <div className={styles.headerCell}>Título del Proyecto</div>
                  <div className={styles.headerCell}>Postulante</div>
                  <div className={styles.headerCell}>Asignación</div>
                  <div className={styles.headerCell}>Evaluación</div>
                  <div className={styles.headerCell}>Acciones</div>
                </div>

                {applications.map((application) => (
                  <div key={application.id} className={styles.tableRow}>
                    <div className={styles.tableCell} data-label="Folio">
                      <span className={styles.applicationNumber}>
                        {application.applicationNumber}
                      </span>
                    </div>

                    <div
                      className={styles.tableCell}
                      data-label="Título del Proyecto"
                    >
                      <div className={styles.projectTitle}>
                        {application.projectTitle}
                      </div>
                      <div className={styles.projectMeta}>
                        Asignada el:{' '}
                        {formatDate(application.assignment.assignedAt)}
                      </div>
                    </div>

                    <div
                      className={styles.tableCell}
                      data-label="Investigador Responsable"
                    >
                      <span className={styles.investigatorName}>
                        {application.principalInvestigator}
                      </span>
                    </div>

                    <div className={styles.tableCell} data-label="Asignación">
                      <div className={styles.invitationCell}>
                        {application.assignment.status === 'declined' &&
                        (application.assignment.declineReason ||
                          application.assignment.declineNote) ? (
                          // Same pattern as the admin table: badge + info icon,
                          // detail shown on hover/focus (not inline).
                          <span className={styles.declinedBadgeWrapper}>
                            {getAssignmentStatusBadge(application)}
                            <span className={styles.helpTooltip} tabIndex={0}>
                              i
                              <span className={styles.tooltipContent}>
                                {(() => {
                                  const label =
                                    DECLINE_REASON_LABELS[
                                      application.assignment
                                        .declineReason as DeclineReason
                                    ] ?? '';
                                  const note = application.assignment.declineNote;
                                  return note ? `${label} — ${note}` : label;
                                })()}
                              </span>
                            </span>
                          </span>
                        ) : (
                          getAssignmentStatusBadge(application)
                        )}
                      </div>
                    </div>

                    <div className={styles.tableCell} data-label="Evaluación">
                      {getEvaluationStatusBadge(application)}
                    </div>

                    <div className={styles.tableCell} data-label="Acciones">
                      {application.assignment.status === 'declined' ? (
                        <span className={styles.noAction}>—</span>
                      ) : application.assignment.conflictStatus === 'conflict_declared' ? (
                        <span className={styles.noAction}>No disponible</span>
                      ) : application.evaluation?.submittedAt ? (
                        // Read-only consult of a finished evaluation: low emphasis
                        <Button
                          variant="secondary"
                          onClick={() => handleEvaluateClick(application.id)}
                          size="sm"
                          className={styles.actionBtn}
                        >
                          Ver Evaluación
                        </Button>
                      ) : application.assignment.status === 'pending' ? (
                        // Pending actions: high emphasis (solid)
                        <Button
                          variant="primary"
                          onClick={() => handleViewInvitationClick(application)}
                          size="sm"
                          icon="chevron_right"
                          iconPos="right"
                          className={styles.actionBtn}
                        >
                          Ver invitación
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          onClick={() => handleEvaluateClick(application.id)}
                          size="sm"
                          icon="chevron_right"
                          iconPos="right"
                          className={styles.actionBtn}
                        >
                          Evaluar
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <Toast message={error} type="error" onClose={() => setError(null)} />
      )}
      {noNewInvitations && (
        <Toast
          message="No se han recibido nuevas invitaciones"
          type="info"
          onClose={() => setNoNewInvitations(false)}
        />
      )}
    </div>
  );
};

export default EvaluatorDashboard;
